import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ClipboardList, Route } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel } from '../../components/DataStates';
import { apiRequest } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { MerchandiserVisit, Organization, PaginatedResponse } from '../../types/domain';

const dateTimeFormatter = new Intl.DateTimeFormat('en-PK', {
  dateStyle: 'medium',
  timeStyle: 'short',
});

export function MerchandisingPage() {
  const { accessToken, isPlatformAdmin, selectedWorkspace } = useAuth();
  const [platformWorkspace, setPlatformWorkspace] = useState('');
  const organizations = useApiCollection<Organization>(
    'merchandising-visit-organizations',
    '/organizations/?page_size=100&ordering=name',
    accessToken,
    isPlatformAdmin,
  );
  const availableOrganizations = useMemo(
    () => (organizations.data?.results ?? []).filter(
      (organization) => organization.is_active
        && (organization.type === 'agency' || organization.type === 'client'),
    ),
    [organizations.data?.results],
  );
  const workspaceId = isPlatformAdmin
    ? platformWorkspace
    : selectedWorkspace?.organizationId ?? '';
  const visits = useQuery({
    queryKey: ['merchandiser-visits', workspaceId, accessToken],
    enabled: Boolean(accessToken && workspaceId),
    queryFn: () => apiRequest<PaginatedResponse<MerchandiserVisit>>(
      `/merchandiser-visits/?workspace=${workspaceId}&page_size=100&ordering=-scheduled_for,-completed_at`,
      accessToken ? { accessToken } : {},
    ),
  });
  const rows = visits.data?.results ?? [];

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Operations</p>
          <h1>Merchandising visits</h1>
          <p>Monitor completed store visits across campaigns. Route planning lives inside each campaign.</p>
        </div>
        <span className="record-count"><CheckCircle2 size={16} /> {visits.data?.count ?? 0} visits</span>
      </header>

      {isPlatformAdmin ? (
        <section className="content-card supervision-workspace-picker">
          <label>
            Operating organisation
            <select onChange={(event) => setPlatformWorkspace(event.target.value)} value={workspaceId}>
              <option value="">Select an agency or direct client</option>
              {availableOrganizations.map((organization) => (
                <option key={organization.id} value={organization.id}>{organization.name}</option>
              ))}
            </select>
          </label>
        </section>
      ) : null}

      {!workspaceId ? (
        <section className="content-card empty-state supervision-empty">
          <ClipboardList size={28} />
          <h2>Select an operating organisation</h2>
          <p>Visit history is scoped to the agency or direct client operating the campaign.</p>
        </section>
      ) : (
        <>
          <div className="section-heading">
            <div><h2>Completed visits</h2><p>Each row is an immutable same-day completion acknowledgement.</p></div>
            <span>{visits.data?.count ?? 0}</span>
          </div>
          <DataPanel loading={visits.isLoading} error={visits.isError} empty={rows.length === 0}>
            <section className="content-card data-card">
              <div className="data-table-wrap">
                <table className="data-table">
                  <thead><tr><th>Visit</th><th>Merchandiser</th><th>Campaign and route</th><th>Store</th><th>Completed</th><th>Notes</th></tr></thead>
                  <tbody>{rows.map((visit) => (
                    <tr key={visit.id}>
                      <td><strong>{visit.reference}</strong><small>{visit.scheduled_for}</small></td>
                      <td>{visit.merchandiser_name}</td>
                      <td><strong>{visit.campaign_name}</strong><small><Route size={13} /> {visit.route_name} · stop {visit.stop_sequence}</small></td>
                      <td><strong>{visit.venue_name}</strong><small>{visit.venue_city}</small></td>
                      <td>{dateTimeFormatter.format(new Date(visit.completed_at))}</td>
                      <td>{visit.notes || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
          </DataPanel>
        </>
      )}
    </>
  );
}
